import {
  CheckCircleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import classNames from 'classnames';
import { FC } from 'react';

import type { BannerState } from './types';

export interface GlobalBannerProps extends BannerState {
  onDismiss: () => void;
}

export const GlobalBanner: FC<GlobalBannerProps> = ({
  visible,
  message,
  type,
  dismissible,
  onDismiss,
}) => {
  if (!visible) {
    return null;
  }

  const typeStyles = {
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      icon: InformationCircleIcon,
      iconColor: 'text-blue-500',
      textColor: 'text-blue-900',
      leftBorder: 'border-l-4 border-l-blue-500',
    },
    warning: {
      bg: 'bg-white',
      border: 'border-gray-200',
      icon: InformationCircleIcon,
      iconColor: 'text-amber-600',
      textColor: 'text-gray-900',
      leftBorder: 'border-l-6 border-l-amber-600',
    },
    success: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      icon: CheckCircleIcon,
      iconColor: 'text-green-500',
      textColor: 'text-green-900',
      leftBorder: 'border-l-4 border-l-green-500',
    },
    error: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      icon: XCircleIcon,
      iconColor: 'text-red-500',
      textColor: 'text-red-900',
      leftBorder: 'border-l-4 border-l-red-500',
    },
  };

  const style = typeStyles[type];
  const Icon = style.icon;

  return (
    <div
      className={classNames(
        style.bg,
        'border-b',
        style.border,
        style.leftBorder,
        'shadow-sm',
      )}
    >
      <div className="flex items-center justify-center px-8 py-4">
        <div className="flex items-center">
          <Icon className={classNames('size-6 shrink-0', style.iconColor)} />
          <p
            className={classNames('ml-3 text-sm font-medium', style.textColor)}
          >
            {message}
          </p>
        </div>
        {dismissible && (
          <button
            onClick={onDismiss}
            className={classNames(
              'ml-4 shrink-0 rounded-md p-1 hover:bg-black/5',
              style.textColor,
            )}
            aria-label="Dismiss banner"
          >
            <XMarkIcon className="size-4" />
          </button>
        )}
      </div>
    </div>
  );
};
